import React, { useState, useEffect } from 'react';

interface FormTextFieldProps {
    label: string;
    name: string;
    value: string;
    onChange: (e: React.FormEvent<HTMLInputElement>) => void;
    disabled?: boolean;
    placeholder?: string;
    required?: boolean;
    type?: 'text' | 'email' | 'password' | 'tel';
    emptyStateText?: string;
    showEmptyState?: boolean;
    error?: string;
    validateField?: () => string | undefined;
    onValidationChange?: (isValid: boolean) => void;
}

/**
 * A reusable form text field component that matches the styling of CustomerDetailsForm
 */
const FormTextField: React.FC<FormTextFieldProps> = ({
    label,
    name,
    value,
    onChange,
    disabled = false,
    placeholder = '',
    required = false,
    type = 'text',
    emptyStateText = 'No data',
    showEmptyState = false,
    error,
    validateField,
    onValidationChange,
}) => {
    const [localError, setLocalError] = useState<string | undefined>(error);
    const showEmptyStateMessage = !disabled && showEmptyState && !value;

    // Update local error state when prop changes
    useEffect(() => {
        setLocalError(error);
    }, [error]);

    // Validate on value change
    useEffect(() => {
        if (!disabled && required && onValidationChange) {
            const isValid = value.trim() !== '';
            onValidationChange(isValid);
        }
    }, [value, required, disabled, onValidationChange]);

    const handleBlur = () => {
        if (!disabled && required && value.trim() === '') {
            setLocalError(`${label} is required`);
            if (onValidationChange) {
                onValidationChange(false);
            }
        } else if (validateField) {
            const validationError = validateField();
            setLocalError(validationError);
            if (onValidationChange) {
                onValidationChange(!validationError);
            }
        } else {
            setLocalError(undefined);
            if (onValidationChange) {
                onValidationChange(true);
            }
        }
    };

    const hasError = !disabled && localError && !showEmptyStateMessage;

    return (
        <div className="w-full my-2 px-4">
            <label className="block text-sm font-semibold text-gray-500 mb-1">
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
            </label>

            {showEmptyStateMessage ? (
                <span className="text-gray-600 italic text-sm mb-2">{emptyStateText}</span>
            ) : (
                <>
                    <input
                        className={`w-full rounded-md border px-2 text-lg font-semibold py-2 focus:ring-1 focus:outline-none ${
                            hasError
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                : 'focus:border-blue-500'
                        }`}
                        type={type}
                        value={value}
                        name={name}
                        disabled={disabled}
                        onChange={onChange}
                        onBlur={handleBlur}
                        placeholder={placeholder}
                        required={required}
                    />
                    {hasError && <p className="mt-1 text-sm text-red-600">{localError}</p>}
                </>
            )}
        </div>
    );
};

export default FormTextField;
